/**
 * CustomCriterionModal Component
 * Modal for creating and editing custom classification criteria
 */

import { useState, useEffect } from 'react';
import type { Criterion, Category } from '../types';
import './CustomCriterionModal.css';

interface CustomCriterionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (criterion: Criterion) => void;
  existingCriterion?: Criterion | null;
  maxCategories: number;
  existingCriteriaNames: string[];
}

export function CustomCriterionModal({
  isOpen,
  onClose,
  onSave,
  existingCriterion,
  maxCategories,
  existingCriteriaNames,
}: CustomCriterionModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState<Category[]>([
    { name: '', description: '', price_min: 0, price_max: 0 },
    { name: '', description: '', price_min: 0, price_max: 0 },
  ]);
  const [errors, setErrors] = useState<string[]>([]);

  // Initialize form with existing criterion data if editing
  useEffect(() => {
    if (existingCriterion) {
      setName(existingCriterion.name);
      setDescription(existingCriterion.description);
      setCategories(existingCriterion.categories);
    } else {
      // Reset form for new criterion
      setName('');
      setDescription('');
      setCategories([
        { name: '', description: '', price_min: 0, price_max: 0 },
        { name: '', description: '', price_min: 0, price_max: 0 },
      ]);
    }
    setErrors([]);
  }, [existingCriterion, isOpen]);

  const handleAddCategory = () => {
    if (categories.length < maxCategories) {
      setCategories([
        ...categories,
        { name: '', description: '', price_min: 0, price_max: 0 },
      ]);
    }
  };

  const handleRemoveCategory = (index: number) => {
    if (categories.length > 2) {
      setCategories(categories.filter((_, i) => i !== index));
    }
  };

  const handleCategoryChange = (
    index: number,
    field: keyof Category,
    value: string | number
  ) => {
    const updatedCategories = [...categories];
    updatedCategories[index] = {
      ...updatedCategories[index],
      [field]: value,
    };
    setCategories(updatedCategories);
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    // Validate name
    if (!name.trim()) {
      newErrors.push('Criterion name is required');
    } else if (name.length < 3) {
      newErrors.push('Criterion name must be at least 3 characters');
    } else if (name.length > 50) {
      newErrors.push('Criterion name must be less than 50 characters');
    }

    // Check for duplicate criterion name (excluding current if editing)
    const nameLower = name.trim().toLowerCase();
    const isDuplicate = existingCriteriaNames.some(
      existingName =>
        existingName.toLowerCase() === nameLower &&
        existingName !== existingCriterion?.name
    );
    if (isDuplicate) {
      newErrors.push('A criterion with this name already exists');
    }

    // Validate description
    if (!description.trim()) {
      newErrors.push('Criterion description is required');
    } else if (description.length < 10) {
      newErrors.push('Description must be at least 10 characters');
    } else if (description.length > 200) {
      newErrors.push('Description must be less than 200 characters');
    }

    // Validate categories
    const filledCategories = categories.filter(
      cat => cat.name.trim() !== '' || cat.description.trim() !== ''
    );

    if (filledCategories.length < 2) {
      newErrors.push('At least 2 categories are required');
    }

    // Validate each filled category
    const categoryNames = new Set<string>();
    filledCategories.forEach((category, index) => {
      if (!category.name.trim()) {
        newErrors.push(`Category ${index + 1}: Name is required`);
      } else if (category.name.length < 2) {
        newErrors.push(`Category ${index + 1}: Name must be at least 2 characters`);
      } else if (category.name.length > 50) {
        newErrors.push(`Category ${index + 1}: Name must be less than 50 characters`);
      }

      // Check for duplicate category names
      const catNameLower = category.name.trim().toLowerCase();
      if (categoryNames.has(catNameLower)) {
        newErrors.push(`Category "${category.name}" is duplicated`);
      }
      categoryNames.add(catNameLower);

      if (!category.description.trim()) {
        newErrors.push(`Category ${index + 1}: Description is required`);
      } else if (category.description.length < 5) {
        newErrors.push(`Category ${index + 1}: Description must be at least 5 characters`);
      } else if (category.description.length > 150) {
        newErrors.push(`Category ${index + 1}: Description must be less than 150 characters`);
      }

      if (category.price_min < 0 || category.price_min > 100) {
        newErrors.push(`Category ${index + 1}: Minimum price must be between 0 and 100`);
      }

      if (category.price_max < 0 || category.price_max > 100) {
        newErrors.push(`Category ${index + 1}: Maximum price must be between 0 and 100`);
      }

      if (category.price_min > category.price_max) {
        newErrors.push(`Category ${index + 1}: Minimum price cannot exceed maximum price`);
      }
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    // Filter out empty categories
    const validCategories = categories.filter(
      cat => cat.name.trim() !== '' && cat.description.trim() !== ''
    );

    const criterion: Criterion = {
      id: existingCriterion?.id || `custom-${Date.now()}`,
      name: name.trim(),
      description: description.trim(),
      categories: validCategories,
      active: existingCriterion?.active ?? true,
      isCustom: true,
    };

    onSave(criterion);
    onClose();
  };

  const handleCancel = () => {
    setErrors([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="custom-modal-overlay" onClick={handleCancel}>
      <div className="custom-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="custom-modal-header">
          <h2>
            {existingCriterion ? 'Edit Custom Criterion' : 'Create Custom Criterion'}
          </h2>
          <button className="custom-modal-close-btn" onClick={handleCancel}>
            ✕
          </button>
        </div>

        <div className="custom-modal-body">
          {errors.length > 0 && (
            <div className="custom-modal-errors">
              <div className="error-header">
                <span className="error-icon">⚠️</span>
                <span>Please fix the following errors:</span>
              </div>
              <ul>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="custom-form-section">
            <label className="custom-form-label">
              Criterion Name <span className="required">*</span>
            </label>
            <input
              type="text"
              className="custom-form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Material Type, Pattern Style, etc."
              maxLength={50}
            />
            <span className="char-count">{name.length}/50</span>
          </div>

          <div className="custom-form-section">
            <label className="custom-form-label">
              Description <span className="required">*</span>
            </label>
            <textarea
              className="custom-form-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this criterion classifies..."
              rows={3}
              maxLength={200}
            />
            <span className="char-count">{description.length}/200</span>
          </div>

          <div className="custom-form-section">
            <div className="categories-header">
              <label className="custom-form-label">
                Categories <span className="required">*</span>
                <span className="category-count-label">
                  ({categories.filter(c => c.name.trim()).length}/{maxCategories})
                </span>
              </label>
              {categories.length < maxCategories && (
                <button className="add-category-btn" onClick={handleAddCategory}>
                  + Add Category
                </button>
              )}
            </div>

            <div className="categories-list">
              {categories.map((category, index) => (
                <div key={index} className="category-form-card">
                  <div className="category-form-header">
                    <span className="category-number">Category {index + 1}</span>
                    {categories.length > 2 && (
                      <button
                        className="remove-category-btn"
                        onClick={() => handleRemoveCategory(index)}
                        title="Remove category"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <input
                    type="text"
                    className="category-form-input"
                    value={category.name}
                    onChange={(e) =>
                      handleCategoryChange(index, 'name', e.target.value)
                    }
                    placeholder="Category name"
                    maxLength={50}
                  />

                  <textarea
                    className="category-form-textarea"
                    value={category.description}
                    onChange={(e) =>
                      handleCategoryChange(index, 'description', e.target.value)
                    }
                    placeholder="Category description"
                    rows={2}
                    maxLength={150}
                  />

                  <div className="price-range-inputs">
                    <div className="price-input-group">
                      <label className="price-input-label">Min Price (€)</label>
                      <input
                        type="number"
                        className="price-input"
                        value={category.price_min}
                        onChange={(e) =>
                          handleCategoryChange(
                            index,
                            'price_min',
                            parseFloat(e.target.value) || 0
                          )
                        }
                        min="0"
                        max="100"
                        step="1"
                      />
                    </div>
                    <div className="price-input-group">
                      <label className="price-input-label">Max Price (€)</label>
                      <input
                        type="number"
                        className="price-input"
                        value={category.price_max}
                        onChange={(e) =>
                          handleCategoryChange(
                            index,
                            'price_max',
                            parseFloat(e.target.value) || 0
                          )
                        }
                        min="0"
                        max="100"
                        step="1"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="custom-modal-footer">
          <button className="custom-modal-cancel-btn" onClick={handleCancel}>
            Cancel
          </button>
          <button className="custom-modal-save-btn" onClick={handleSave}>
            {existingCriterion ? 'Save Changes' : 'Create Criterion'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CustomCriterionModal;
